import {
  type BuyerVatRates,
  convertMinorUnitToValue,
  createMinorUnit,
  DEFAULT_COUNTRY,
  getTax,
  getVatRateByCountryCode,
  isNullable,
  LEGACY_DELIVERY_METHOD_ID,
  resolveDefaultShippingMethods,
  roundNumber,
  shippingForBuyer
} from 'shared-utils'
import {
  type CustomerType,
  type Language,
  type MinorUnit,
  type Option,
  type ResolvedShippingMethod,
  type ShippingMethodKind,
  type ShippingOption,
  type ShippingProviderConfig,
  type ShippingRate,
  type ShippingRuleContext
} from 'ecosystem'
import { EXTRA_FEE, MINIMUM_ORDER_AMOUNT } from '../constants'

const localeDictionary = {
  en: {
    freeDeliveryLabel: 'Free - Delivery with telephone notification',
    paidDeliveryLabel: 'Shipping - Delivery with telephone notification'
  },
  sv: {
    freeDeliveryLabel: 'Gratis - Leverans med telefonavisering',
    paidDeliveryLabel: 'Frakt - Leverans med telefonavisering'
  },
  no: {
    freeDeliveryLabel: 'Gratis - Levering med telefonvarsel',
    paidDeliveryLabel: 'Frakt - Levering med telefonvarsel'
  },
  da: {
    freeDeliveryLabel: 'Gratis - Levering med telefonisk advisering',
    paidDeliveryLabel: 'Fragt - Levering med telefonisk advisering'
  },
  de: {
    freeDeliveryLabel: 'Kostenlos - Lieferung mit telefonischer Benachrichtigung',
    paidDeliveryLabel: 'Versand - Lieferung mit telefonischer Benachrichtigung'
  },
  fi: {
    freeDeliveryLabel: 'Ilmainen - Toimitus puhelinilmoituksella',
    paidDeliveryLabel: 'Toimitus - Toimitus puhelinilmoituksella'
  }
}

/**
 * The store's flat rate, with the historical defaults applied. This is the
 * terminal fallback of the resolution chain — a conditional rate wins over it,
 * and it wins over nothing at all — so a store that configures no rules, or
 * only half of `simpleShipping`, is priced exactly as it is today.
 */
const storeFallbackRate = (opts?: ShippingProviderConfig['simpleShipping']): ShippingRate => ({
  deliveryFee: opts?.deliveryFee ?? EXTRA_FEE,
  minimumOrderAmountForFreeDelivery:
    opts?.minimumOrderAmountForFreeDelivery ?? MINIMUM_ORDER_AMOUNT,
  ...(opts?.labels ? { labels: opts.labels } : {})
})

const resolveName = (method: ResolvedShippingMethod, locale: Language, isFree: boolean): string => {
  const rateLabels = method.rate?.labels
  const configured = isFree ? rateLabels?.freeDeliveryLabel : rateLabels?.paidDeliveryLabel
  if (configured) {
    return configured
  }

  // A merchant-configured method names itself; the implicit legacy one falls
  // back to the locale dictionary, as it always has.
  if (method.id !== LEGACY_DELIVERY_METHOD_ID) {
    return method.label
  }

  const dictionary = localeDictionary[locale]
  return isFree ? dictionary.freeDeliveryLabel : dictionary.paidDeliveryLabel
}

export interface PaymentShippingOptionsFactoryParams {
  /** The basket as the buyer is charged for it, VAT-inclusive. */
  orderAmount: MinorUnit
  /** The same basket net of VAT. Only rules declared `NET` read it, but pass it
   *  wherever it is known — a NET rule with no net amount silently prices as
   *  GROSS. */
  netOrderAmount?: MinorUnit
  locale: Language
  /** The *store's* country — drives the domestic VAT rate on freight. Not a
   *  rule input; use `recipientCountryCode` for that. */
  countryCode?: string
  /** Store-level flat rate. Still the fallback for every unconditional store. */
  opts?: ShippingProviderConfig['simpleShipping']
  /** Resolved VAT treatment of the buyer. The configured `deliveryFee` is a
   *  VAT-inclusive figure at the store's own rate, so a zero-rated buyer needs
   *  it brought down to net rather than merely relabelled 0 %. */
  buyerVat?: Option<BuyerVatRates>
  /** Full shipping config — read for `defaultOptions.methods`. Omit to get the
   *  legacy single flat option. */
  shippingConfig?: Option<ShippingProviderConfig>
  /** Recipient country, when known. Rules that key off it never match while it
   *  is unknown, so pricing falls through to the store's flat rate. */
  recipientCountryCode?: Option<string>
  customerType?: CustomerType
  /** Whether `store.pickups` has anything in it. Only affects the implicit
   *  option set a store with no configured methods falls back to. */
  pickupAvailable?: boolean
  /** Labels for those implicit options — the FE passes its own translated
   *  strings; the payment row itself keeps using the locale dictionary. */
  deliveryLabel?: string
  pickupLabel?: string
}

export interface PaymentShippingFactoryParams extends PaymentShippingOptionsFactoryParams {
  /** The method the shopper picked, when a delivery step ran before this call.
   *  Falls back to the preselected one. */
  methodId?: string
}

/**
 * A delivery option offered to the shopper: the payment-row fields every
 * provider needs, plus what the FE selector has to render.
 */
export interface PricedShippingOption extends ShippingOption {
  /** Inherited `price` / `tax_amount` are in MINOR units, like every other
   *  payment row. Convert with `convertMinorUnitToValue` before displaying. */
  kind: ShippingMethodKind
  /** The method's own name, before the free/paid label substitution. */
  label: string
  description?: string
  pickupIds?: Option<string[]>
  isFree: boolean
  preselected: boolean
}

/**
 * Every delivery option available to this buyer right now, priced and
 * VAT-adjusted. A store with no `defaultOptions.methods` resolves to the
 * single implicit option (plus pickup, when the caller says the store has
 * pickup locations) that the DEFAULT branch has always offered.
 */
export const paymentShippingOptionsFactory = ({
  orderAmount,
  netOrderAmount,
  locale,
  countryCode = DEFAULT_COUNTRY,
  opts,
  buyerVat,
  shippingConfig,
  recipientCountryCode,
  customerType,
  pickupAvailable,
  deliveryLabel,
  pickupLabel
}: PaymentShippingOptionsFactoryParams): PricedShippingOption[] => {
  const ruleContext: ShippingRuleContext = {
    orderAmount: convertMinorUnitToValue(orderAmount),
    ...(isNullable(netOrderAmount)
      ? {}
      : { netOrderAmount: convertMinorUnitToValue(netOrderAmount) }),
    countryCode: recipientCountryCode ?? buyerVat?.buyerCountry,
    ...(customerType ? { customerType } : {}),
    ...(buyerVat?.treatment ? { vatTreatment: buyerVat.treatment } : {})
  }

  const methods = resolveDefaultShippingMethods(shippingConfig, ruleContext, {
    fallbackRate: storeFallbackRate(opts),
    pickupAvailable,
    deliveryLabel,
    pickupLabel
  })

  const domesticRate = getVatRateByCountryCode(countryCode)

  return methods.map((method) => {
    // A GROSS rate is what this has always been: a VAT-inclusive figure at the
    // store's own rate. A NET rate is the merchant's ex-VAT number, so it is
    // grossed up at that same rate before the buyer rebase — and handed to
    // `shippingForBuyer` as the exact net too, so a zero-rated buyer lands back
    // on the figure that was configured instead of a rounded round-trip.
    //
    // NOTE: on a GROSS rate the free-delivery threshold is still compared
    // against whatever the buyer is actually charged, so a zero-rated buyer
    // reaches it on a larger basket than a domestic one. Declaring the rate NET
    // is what makes that threshold buyer-invariant.
    const netFee = method.vatBasis === 'NET' ? createMinorUnit(method.price) : null
    const grossFee =
      netFee === null
        ? createMinorUnit(method.price)
        : createMinorUnit(roundNumber(method.price * (1 + domesticRate / 100), 2))
    const { cost: unitPrice, vatRate } = shippingForBuyer(grossFee, domesticRate, buyerVat, netFee)
    const taxRate = createMinorUnit(vatRate)
    const quantity = 1
    const totalDiscountAmount = 0
    const totalAmount = quantity * unitPrice - totalDiscountAmount

    return {
      id: method.id,
      name: resolveName(method, locale, method.isFree),
      price: unitPrice,
      tax_amount: getTax(totalAmount, taxRate, true),
      tax_rate: taxRate,
      kind: method.kind,
      label: method.label,
      ...(method.description ? { description: method.description } : {}),
      ...(method.pickupIds ? { pickupIds: method.pickupIds } : {}),
      isFree: method.isFree,
      preselected: method.preselected
    }
  })
}

/**
 * Use paymentShippingFactory if there is no third party shipping providers (like Ingrid)
 *
 * @remarks
 * The single priced row for the option the shopper picked — or the preselected
 * one when no delivery step has run yet. A store that configures no delivery
 * options gets the flat rate this function has always returned.
 */
export const paymentShippingFactory = ({
  methodId,
  ...params
}: PaymentShippingFactoryParams): ShippingOption => {
  const options = paymentShippingOptionsFactory(params)
  const { kind, label, description, pickupIds, isFree, preselected, ...option } =
    options.find((candidate) => candidate.id === methodId) ??
    options.find((candidate) => candidate.preselected) ??
    options[0]

  // The row's id is the *payment protocol* reference, not the business one.
  // Every provider's provisioning step recovers `shippingCost` by finding the
  // freight row again (Svea by `ArticleNumber`, Nexi by `reference`), so it has
  // to stay the constant those matchers expect. Which option the shopper chose
  // is recorded on the order itself — `defaultDetails.selectedMethod` and
  // `ShipmentDTO.carrierService`.
  return { ...option, id: LEGACY_DELIVERY_METHOD_ID }
}
